import type { NullableNumber } from '~/common/types/nullable.type.ts';
import type { OrderType } from '~/common/types/order.type';
import type { PaginationMeta, PaginationType } from '~/common/types/pagination.type';

export enum ErrorType {
  CUSTOM = 1,
  VALIDATOR = 0,
}

export interface ApiResponse<T = unknown> {
  status: number;
  data: T;
}

export interface PaginatedApiResponse<PageT = unknown> {
  type: PaginationType;
  status: number;
  data: PageT[];
  meta: PaginationMeta;
}

interface BaseErrorResponse {
  statusCode: number;
  type: ErrorType;
}

export interface CustomErrorResponse extends BaseErrorResponse {
  errorCode: string;
  status: string;
  message: string;
  fieldName?: string;
}

export interface ValidatorErrorResponse extends BaseErrorResponse {
  errors: {
    errorCode: string;
    status: string;
    message: string;
    fieldName?: string;
  }[];
}

export type ApiErrorResponseData = CustomErrorResponse | ValidatorErrorResponse;

/**
 * 페이지네이션 + 검색어 조회 API 파라미터
 * @param domainId 도메인 ID
 * @param page 조회하려는 페이지 번호
 * @param perPage 페이지당 조회할 데이터 개수
 * @param order 정렬 방식 'ASC' | 'DESC'
 * @param orderKey 정렬 기준
 * @param key
 * @param searchText 검색어
 */
export interface PaginatedApiParamsWithSearch<OrderKey, Key> extends PaginatedApiParams<OrderKey> {
  key?: Key;
  searchText?: string;
}

/**
 * 페이지네이션 조회 API 파라미터
 * @param page 조회하려는 페이지 번호
 * @param perPage 페이지당 조회할 데이터 개수
 * @param order 정렬 방식 'ASC' | 'DESC'
 * @param orderKey 정렬 기준
 */
export interface PaginatedApiParams<OrderKey> {
  page: number;
  perPage?: number;
  order?: OrderType;
  orderKey?: OrderKey;
}

/**
 * 대규모 로그 데이터 조회 API 파라미터
 * @param domainId 도메인 ID
 * @param offset 데이터 시작 지점
 * @param limit 데이터 개수
 * @param order 정렬 방식 'ASC' | 'DESC'
 * @param orderKey 정렬 기준
 */
export interface OffsetApiParams<OrderKey> {
  domainId: number;
  offset?: number;
  limit?: number;
  order?: OrderType;
  orderKey?: OrderKey;
}

export type OffsetMeta<CustomData = void> = CustomData extends void
  ? {
      current: number;
      prev: NullableNumber;
      next: NullableNumber;
      limit: number;
    }
  : {
      current: number;
      prev: NullableNumber;
      next: NullableNumber;
      limit: number;
      data: CustomData;
    };

export interface OffsetApiResponse<T = unknown> {
  type: PaginationType;
  status: number;
  data: T[];
  meta: OffsetMeta;
}

export interface LinkDownloadResponse {
  downloadUrl: string;
  expiresAt: string;
}
